import { createFileRoute, Link } from '@tanstack/react-router'
import { Section } from '@/components/ui/section'
import { Button } from '@/components/ui/button'
import { TrendingUp, BarChart3, Microscope, Layers, ArrowRight } from 'lucide-react'

export const Route = createFileRoute('/order-flow-labs')({
  component: OrderFlowLabsComponent,
})

function OrderFlowLabsComponent() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero */}
      <section className="relative py-32 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-blue-950/20 -z-10" />
        <div className="absolute inset-0 bg-[url('https://grain-url-placeholder')] opacity-20" /> {/* Texture placeholder */}

        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-sm font-medium text-teal-400 mb-6">
              <Microscope className="mr-2 h-4 w-4" />
              Advanced Analysis
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6">
              Order Flow <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-500">
                Labs
              </span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 text-balance">
              See what others can't. Deconstruct the market auction with AI-powered heatmap analysis, iceberg detection, and liquidity modeling.
            </p>
            <div className="flex gap-4">
              <Link to="/marketplace">
                <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white">
                  Access Tools
                </Button>
              </Link>
              <Link to="/education">
                <Button size="lg" variant="outline">
                  Learn the Methodology
                </Button>
              </Link>
            </div>
          </div>

          {/* Visual Placeholder for Heatmap/DOM */}
          <div className="relative aspect-square lg:aspect-video rounded-2xl bg-black/50 border border-white/10 overflow-hidden shadow-2xl shadow-teal-900/20">
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Simulated Order Flow Heatmap UI */}
              <div className="w-full h-full p-4 grid grid-cols-12 grid-rows-6 gap-1 opacity-50">
                {[...Array(72)].map((_, i) => (
                  <div
                    key={i}
                    className="rounded-sm"
                    style={{
                      backgroundColor: Math.random() > 0.7 ? '#14b8a6' : Math.random() > 0.8 ? '#ef4444' : '#ffffff05',
                      opacity: Math.random()
                    }}
                  />
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm bg-black/30">
                <div className="text-center">
                  <TrendingUp className="h-16 w-16 text-teal-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold">Market Depth Visualization</h3>
                  <p className="text-sm text-gray-400">Live simulation preview</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <Section className="bg-background">
        <div className="grid md:grid-cols-3 gap-8">
          <FeatureBlock
            icon={<Layers className="h-8 w-8 text-teal-400" />}
            title="Liquidity Heatmaps"
            description="Visualize resting limit orders before price arrives. Identify support and resistance zones created by institutional interest."
          />
          <FeatureBlock
            icon={<BarChart3 className="h-8 w-8 text-purple-400" />}
            title="Footprint Charts"
            description="Look inside the candle. See aggressive buying and selling at every price tick to spot exhaustion and absorption."
          />
          <FeatureBlock
            icon={<Microscope className="h-8 w-8 text-blue-400" />}
            title="Iceberg Detection"
            description="AI algorithms that detect hidden size. Know when large players are filling positions without revealing their hand."
          />
        </div>
      </Section>
    </div>
  )
}

function FeatureBlock({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) {
  return (
    <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-teal-500/50 transition-colors">
      <div className="mb-6">{icon}</div>
      <h3 className="text-2xl font-bold mb-4">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">
        {description}
      </p>
    </div>
  )
}
